import _ from "lodash";
import { action, makeObservable } from "mobx";
import React from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  formatDateTime,
  formatTime,
  toLuxon,
} from "../../shared/dateTimeUtils";
import Widget from "../Widget";
import MonitorStore from "./MonitorStore";
import TimeModal from "./TimeModal";
import config from "../../config";
import { DateTime } from "luxon";
const reactSwal = withReactContent(Swal);
export default class MonitorSuper extends Widget {
  eventTarget = new EventTarget();
  constructor(props) {
    super(props);

    makeObservable(this, {
      inertialScroll: action,
      wheel: action,
      zoomChange: action,
      showHistorySelector: action,
    });

    // This persists the monitorStore on the global store (appStore), and invalidates based on ASMPID.
    if (!props.isEmbedded && props.store.monitorStore?.asmpid === this.asmpid) {
      this.store = this.props.store.monitorStore;
    } else {
      this.store = new MonitorStore({
        asmpid: this.asmpid,
        loadSession: !!this.props.match.params.timestamp,
        suppressDiscreteRow: this.props.suppressDiscreteRow,
        isEmbedded: this.props.isEmbedded,
      });
      if (!props.isEmbedded) this.props.store.monitorStore = this.store;
    }

    const isTimestamped = this.observeRouteTimestamp(props);
    if (isTimestamped) {
      this.store.liveMode = false;
    }
    if (!props.isEmbedded) {
      this.patientRefreshInterval = setInterval(() => {
        this.getPatient(this.asmpid);
      }, 60 * 1000);
    }
    if (props.zoom) {
      this.store.zoom = this.props.zoom;
    }

    this.dragCharts = this.frameDebounce(this.dragCharts);
    this.zoomChange = this.frameDebounce(this.zoomChange.bind(this));
    this.pinchStart = this.pinchStart.bind(this);
    this.pinchEnd = this.pinchEnd.bind(this);
    this.pinch = this.frameDebounce(this.pinch.bind(this)).bind(this);
    this.wheel = this.frameDebounce(this.wheel.bind(this)).bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      _.get(this.props, "match.params.timestamp") !==
      _.get(nextProps, "match.params.timestamp")
    ) {
      this.observeRouteTimestamp(nextProps);
    }
  }

  componentDidMount() {
    super.componentDidMount();
    if (_.get(this.props.store, "patientStore.isSnippetWorklist")) {
      this.props.store.hideMainNav = true;
    }
  }
  observeRouteTimestamp(props = this.props) {
    if (props.match.params.timestamp) {
      this.store.liveMode = false;
      const chartEndTime = DateTime.fromISO(
        decodeURIComponent(props.match.params.timestamp),
      );
      if (chartEndTime?.isValid) {
        this.store.chartEndTime = this.store.originalChartEndTime =
          chartEndTime;
        return true;
      }
    }
  }

  componentDidUpdate() {
    if (this.refCanvas && !this.wheelBound) {
      this.refCanvas.addEventListener("wheel", this.wheel);
      this.wheelBound = true;
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    clearInterval(this.patientRefreshInterval);
  }

  time(time) {
    return formatTime(time);
  }

  pause() {
    this.store.liveMode = false;
    this.props.measurementSource?.pause();
  }

  canvasMouseDown = async (e) => {
    if (e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.props.liveOnly) return;

    let { x, y } = this.getXY(e);
    if (!x || !y) {
      console.error("Cannot detect pointer location.");
      return;
    }
    this.pause();

    this.store.mouseDownX = x;
    this.store.dragging = true;

    this.store.originalChartEndTime = this.store.chartEndTime;
    this.store.originalStripEndTime = this.store.stripEndTime;
    this.store.mouseDownY = y + this.store.offsetY;

    if (e.rhythmStrip) {
      this.draggingStrip = true;
    }
    if (e.touches) {
      window.addEventListener("touchmove", this.dragCharts, { passive: false });
      window.addEventListener("touchend", this.endDrag);
    } else {
      window.addEventListener("mousemove", this.dragCharts);
      window.addEventListener("mouseup", this.endDrag);
    }

    this.store.dragEvent = undefined;
    this.store.offsetX = 0;

    return false;
  };

  getXY(e) {
    return {
      x: e.clientX || e.pageX || _.get(e, "touches[0].pageX"),
      y: e.clientY || e.pageY || _.get(e, "touches[0].pageY"),
    };
  }

  dragCharts = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.store.dragging && !this.props.liveOnly) {
      return;
    }
    if (_.get(e, "touches.length") > 1) {
      return;
    }
    const { x, y } = this.getXY(e);
    if (!x || !y) {
      return;
    }
    const deltaX = this.store.mouseDownX - x;

    if (this.panToLiveMode?.(-deltaX)) return;

    // Polyfill movementX/Y for touch.
    if (!e.movementX && this.store.dragEvent) {
      let prevCoords = this.getXY(this.store.dragEvent);
      e = {
        ...e,
        movementX: x - prevCoords.x,
        movementY: y - prevCoords.y,
      };
    }

    if (!this.props.liveOnly) {
      if (this.draggingStrip) {
        this.store.offsetX =
          (deltaX * this.store.pixelsPerSecond) / this.store.minPixelsPerSecond;
      } else {
        this.store.offsetX = deltaX;
      }
    }

    this.store.dragEvent = e;
    this.syncBoundaryPositions?.();
    const offsetY = this.store.mouseDownY - y;
    if (this.props.liveOnly || Math.abs(offsetY) > Math.abs(deltaX)) {
      this.store.offsetY = this.store.mouseDownY - y;
    }
    this.renderCharts();
  };
  endDrag = () => {
    if (!this.store.dragging && !this.props.liveOnly) {
      return;
    }
    this.store.dragging = false;
    this.draggingStrip = false;
    window.removeEventListener("mousemove", this.dragCharts);
    window.removeEventListener("touchmove", this.dragCharts);
    window.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("touchend", this.endDrag);
    if (this.store.dragEvent && !this.store.liveOnly) {
      let { movementX, movementY } = this.store.dragEvent;
      this.inertialScroll({ movementX, movementY });
    }
  };

  inertialScroll({ movementX }) {
    if (movementX < 1 && movementX > -1) {
      this.inertialScrolling = false;
      this.store.consumeOffsetX();
      return;
    }
    if (this.store.liveMode || this.panToLiveMode?.(movementX)) return;
    this.inertialScrolling = true;
    const inertia = 0.9;
    if (movementX && !this.store.dragging) {
      requestAnimationFrame(() => {
        movementX *= inertia;
        this.store.offsetX -= movementX;
        this.inertialScroll({ movementX });
        this.renderCharts();
      });
    } else if (!this.store.dragging) {
      this.inertialScrolling = false;
      this.store.mouseDownX = undefined;
    }
    if (this.store.markers.length) {
      this.store.markers.forEach(
        (marker, i) => (this.store.markers[i] = { ...marker }),
      );
      if (this.store.caliperMode) this.calcDistances();
      if (this.store.measurementMode && this.syncBoundaryPositions)
        this.syncBoundaryPositions();
    }
  }

  wheel(e) {
    e && e.preventDefault();
    // derive the amount to zoom.
    const z = e.deltaY * -0.01;
    this.zoomingTriggered = performance.now();
    const newZoom = Math.max(
      config.pm.zoom?.min || 1,
      Math.min(config.pm.zoom?.max || 10, this.store.zoom + z),
    );
    if (this.store.zoom !== newZoom) {
      this.store.zoom = newZoom;
      this.canvasPoints = {};

      const scaleFactor = z / this.store.pixelsPerSecond;
      this.store.originalChartEndTime =
        this.store.originalChartEndTime || this.store.chartEndTime;

      // center on mouse horizontal
      // update offsets.
      const canvas = e.currentTarget;
      const bounds = canvas.getBoundingClientRect();
      const yPos = e.clientY - bounds.y;
      this.store.offsetY += scaleFactor * (yPos + this.store.offsetY);

      // TODO: center on mouse position.
      // const xPos = e.clientX - bounds.x;
      // const percentFromRight = (canvas.width - xPos) / xPos;
      // this.store.offsetX -= scaleFactor * percentFromRight * canvas.width;
    }
    if (this.syncBoundaryPositions) {
      this.syncBoundaryPositions();
      this.syncCaliperPositions();
    }
    this.renderCharts();
  }

  pinch(e) {
    e && e.preventDefault();
    if (!this.pinchStartEvent) {
      return;
    }
    // Derive the amount to zoom.
    const newScale = e.scale; // this.pinchStartEvent.scale;
    this.canvasPoints = {};
    this.store.pixelsPerSecond = _.clamp(
      this.initialPixelPerSecond * newScale,
      this.store.minPixelsPerSecond,
      this.store.maxPixelsPerSecond,
    );
    this.store.originalChartEndTime =
      this.store.originalChartEndTime || this.store.chartEndTime;

    if (this.syncBoundaryPositions) {
      this.syncBoundaryPositions();
      this.syncCaliperPositions();
    }
    this.renderCharts();
  }
  pinchStart(e) {
    e && e.preventDefault();
    this._zooming = true;
    this.pinchStartEvent = e;
    this.initialPixelPerSecond = this.store.pixelsPerSecond;
    this.store.originalChartEndTime = this.store.chartEndTime;
  }
  pinchEnd(e) {
    e && e.preventDefault();
    this._zooming = false;
    this.pinchStartEvent = undefined;
    this.initialPixelPerSecond = undefined;
    this.store.originalChartEndTime = undefined;
  }
  get zooming() {
    return !!(
      this._zooming ||
      (this.zoomingTriggered &&
        this.zoomingTriggered > performance.now() - 1000)
    );
  }

  zoomChange(e) {
    this.store.zoom = e.target.value;
    this.zoomingTriggered = performance.now();

    if (this.syncCaliperPositions) {
      this.syncCaliperPositions();
    }
    if (this.syncBoundaryPositions) {
      this.syncBoundaryPositions();
    }
    this.renderCharts();
  }

  showHistorySelector = action((e) => {
    e && e.preventDefault();

    let timeData;
    const hours = _.get(
      this.props.store.prefs,
      "userPreferences.server.pm.dataRangeInHours",
    );
    if (hours) {
      const reactSwalConfig = {
        title: formatDateTime(toLuxon(this.store.chartEndTime).toISO()),
        html: (
          <TimeModal
            value={toLuxon(this.store.chartEndTime).toISO()}
            now={toLuxon(_.get(this.store.measurements, "serverTimestamp"))}
            latency={this.store.serverLatency}
            hours={hours}
            onChange={(newTime) => {
              timeData = newTime;
            }}
          />
        ),
        customClass: "time-selector",
        showCancelButton: true,
        allowEnterKey: true,
      };

      return reactSwal.fire(reactSwalConfig).then((res) => {
        if (res.dismiss) return Promise.reject();
        const newTime = toLuxon(timeData);
        if (newTime.isValid) {
          this.gotoTime(newTime);
          this.eventTarget.dispatchEvent(
            new CustomEvent("timeSelect", {
              detail: {
                newTime,
              },
            }),
          );
          return newTime;
        } else {
          return Promise.reject();
        }
      });
    } else {
      return Promise.reject();
    }
  });

  gotoTime(newTime) {
    this.canvasWorker?.postMessage({ resetLiveMode: { force: true } });
    this.store.resetMeasurements();
    this.store.liveMode = false;
    if (this.store.stripEndTime) {
      const stripEndTimeShiftMillis = newTime
        .diff(toLuxon(this.store.chartEndTime), "milliseconds")
        .toObject().milliseconds;
      this.store.stripEndTime = toLuxon(this.store.stripEndTime).plus({
        milliseconds: stripEndTimeShiftMillis,
      });
    }
    this.store.chartEndTime = newTime;
  }
}
